import { useCallback, useContext } from 'react';
import { useRecoilValue } from 'recoil';
import { ENDPOINTS } from 'pages/common/consts/endpoints';
import { REGION_ID_COOKIE } from 'pages/common/consts/cookies';
import { roundToTwo } from 'common/helpers/round-to-two.helper';
import { DataContext } from 'entries/common/widgets/saved-project-configuration/context';
import { getCookie } from 'utils/cookie.helper';
import { useRegionMismatch } from '../components/AddToCart/hooks/use-region-mismatch';
import { getGeneralItemsFromCalculationState } from '../helpers/get-general-items-from-calculation-state';
import { getGeneralItemsForPatch } from '../helpers/get-general-items-for-patch';
import { getPatchProjectConfigurationBody } from '../helpers/get-patch-project-configuration-body';
import { savedConfigurationCalculationState } from '../store/calculation';
import { TPatchProjectPayload } from '../types';
import { useTotalPrices } from './use-total-prices';

export const usePatchProjectFromCalculationState = () => {
  const { data, setData } = useContext(DataContext);
  const calculationFormState = useRecoilValue(savedConfigurationCalculationState);
  const { totalPrice } = useTotalPrices();
  const { isRegionMismatch } = useRegionMismatch();

  const patchProjectCallBack = useCallback(
    async (payload: TPatchProjectPayload) => {
      if (!data?.api) {
        return;
      }

      const generalItems = getGeneralItemsFromCalculationState(calculationFormState);
      const generalItemsForPatch = getGeneralItemsForPatch(generalItems);
      const regionId = isRegionMismatch
        ? String(data.presaleProps?.regionId ?? getCookie(REGION_ID_COOKIE))
        : undefined;

      try {
        await data.api.patch(
          ENDPOINTS.EDIT_CONFIGURATION(data.id),
          getPatchProjectConfigurationBody({
            payload,
            approximateTotalAmount: roundToTwo(totalPrice),
            configurationParameters: data.projectConfigurationParameters ?? [],
            generalItems: generalItemsForPatch,
            regionId,
          })
        );

        setData(prev => ({
          ...prev,
          generalItems,
          ...(regionId ? { regionId: Number(regionId) } : {}),
        }));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Patch configuration failed', e);
      }
    },
    [calculationFormState, data, isRegionMismatch, setData, totalPrice]
  );

  return { patchProjectCallBack };
};
